import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  type UpcomingPortfolioEvent,
  UPCOMING_EVENT_TYPE_CONFIG,
  CATEGORY_COLORS,
  groupEventsByDate,
} from '../../../mocks/upcomingEventsMock';

interface UpcomingCalendarModuleProps {
  events: UpcomingPortfolioEvent[];
  onEventClick?: (event: UpcomingPortfolioEvent) => void;
  onOpenCalendar?: () => void;
}

const CELL_SIZE = 14;
const CELL_GAP = 3;
const WEEKS_VISIBLE = 26; // ~6 months
const DAYS_OF_WEEK = ['Mon', '', 'Wed', '', 'Fri', '', ''];

function getDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getIntensityColor(count: number, maxSeverity: string): string {
  if (count === 0) return 'var(--color-surface-hover)';
  // Color based on max severity of events on that day
  const baseColors: Record<string, string[]> = {
    critical: ['rgba(239,68,68,0.3)', 'rgba(239,68,68,0.55)', 'rgba(239,68,68,0.8)', 'rgba(239,68,68,1)'],
    high: ['rgba(249,115,22,0.3)', 'rgba(249,115,22,0.55)', 'rgba(249,115,22,0.8)', 'rgba(249,115,22,1)'],
    medium: ['rgba(59,130,246,0.3)', 'rgba(59,130,246,0.55)', 'rgba(59,130,246,0.8)', 'rgba(59,130,246,1)'],
    low: ['rgba(23,196,128,0.3)', 'rgba(23,196,128,0.55)', 'rgba(23,196,128,0.8)', 'rgba(23,196,128,1)'],
  };
  const palette = baseColors[maxSeverity] || baseColors.medium;
  const idx = Math.min(count - 1, palette.length - 1);
  return palette[idx];
}

const UpcomingCalendarModule: React.FC<UpcomingCalendarModuleProps> = ({
  events,
  onEventClick,
  onOpenCalendar,
}) => {
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  // Build grid: start from today, go forward WEEKS_VISIBLE weeks
  const { cells, monthLabels } = useMemo(() => {
    const today = new Date();
    // Start from the most recent Monday
    const startDay = new Date(today);
    const dayOfWeek = startDay.getDay();
    const diff = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : -(dayOfWeek - 1));
    startDay.setDate(startDay.getDate() + diff);

    const totalDays = WEEKS_VISIBLE * 7;
    const grid: Array<{
      date: Date;
      key: string;
      weekIdx: number;
      dayIdx: number;
      count: number;
      maxSeverity: string;
      events: UpcomingPortfolioEvent[];
    }> = [];

    const months: Array<{ label: string; weekIdx: number }> = [];
    let lastMonth = -1;

    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDay);
      d.setDate(d.getDate() + i);
      const weekIdx = Math.floor(i / 7);
      const dayIdx = i % 7;
      const key = getDateKey(d);
      const dayEvents = eventsByDate.get(key) || [];

      // Determine max severity
      const sevOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      let maxSev = 'low';
      dayEvents.forEach((e) => {
        if ((sevOrder[e.severity] ?? 4) < (sevOrder[maxSev] ?? 4)) {
          maxSev = e.severity;
        }
      });

      grid.push({
        date: d,
        key,
        weekIdx,
        dayIdx,
        count: dayEvents.length,
        maxSeverity: maxSev,
        events: dayEvents,
      });

      // Month labels at first occurrence in row 0
      if (d.getMonth() !== lastMonth && dayIdx === 0) {
        months.push({
          label: d.toLocaleDateString('en-US', { month: 'short' }),
          weekIdx,
        });
        lastMonth = d.getMonth();
      }
    }

    return { cells: grid, monthLabels: months };
  }, [eventsByDate]);

  const hoveredEvents = useMemo(() => {
    if (!hoveredDate) return [];
    return eventsByDate.get(hoveredDate) || [];
  }, [hoveredDate, eventsByDate]);

  const totalUpcoming = events.length;
  const next30Days = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + 30);
    return events.filter((e) => new Date(e.due_date) <= cutoff).length;
  }, [events]);

  const svgWidth = WEEKS_VISIBLE * (CELL_SIZE + CELL_GAP) + 30;
  const svgHeight = 7 * (CELL_SIZE + CELL_GAP) + 24;

  return (
    <div className="space-y-3">
      {/* Summary stats */}
      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        <span>
          <strong className="text-[#17c480]">{next30Days}</strong> events in next 30 days
        </span>
        <span>
          <strong style={{ color: 'var(--color-text-primary)' }}>{totalUpcoming}</strong> total upcoming
        </span>
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto">
        <svg width={svgWidth} height={svgHeight} className="block">
          {/* Day-of-week labels */}
          {DAYS_OF_WEEK.map((label, i) => (
            label && (
              <text
                key={i}
                x={0}
                y={24 + i * (CELL_SIZE + CELL_GAP) + CELL_SIZE / 2}
                dominantBaseline="middle"
                fill="var(--color-text-muted)"
                fontSize={9}
              >
                {label}
              </text>
            )
          ))}

          {/* Month labels */}
          {monthLabels.map(({ label, weekIdx }) => (
            <text
              key={`${label}-${weekIdx}`}
              x={30 + weekIdx * (CELL_SIZE + CELL_GAP)}
              y={14}
              fill="var(--color-text-muted)"
              fontSize={10}
            >
              {label}
            </text>
          ))}

          {/* Day cells */}
          {cells.map((cell) => (
            <rect
              key={cell.key}
              x={30 + cell.weekIdx * (CELL_SIZE + CELL_GAP)}
              y={24 + cell.dayIdx * (CELL_SIZE + CELL_GAP)}
              width={CELL_SIZE}
              height={CELL_SIZE}
              rx={2}
              fill={getIntensityColor(cell.count, cell.maxSeverity)}
              stroke={hoveredDate === cell.key ? 'var(--color-text-primary)' : 'transparent'}
              strokeWidth={1}
              className="cursor-pointer transition-all"
              onMouseEnter={(e) => {
                setHoveredDate(cell.key);
                const rect = (e.target as SVGRectElement).getBoundingClientRect();
                setTooltipPos({ x: rect.left, y: rect.top - 4 });
              }}
              onMouseLeave={() => {
                setHoveredDate(null);
                setTooltipPos(null);
              }}
              onClick={() => {
                if (cell.events.length === 1 && onEventClick) {
                  onEventClick(cell.events[0]);
                } else if (cell.events.length > 0 && onOpenCalendar) {
                  onOpenCalendar();
                }
              }}
            />
          ))}
        </svg>
      </div>

      {/* Tooltip */}
      {hoveredDate && tooltipPos && hoveredEvents.length > 0 && (
        <div
          className="fixed z-50 px-3 py-2 rounded-lg text-xs shadow-lg pointer-events-none"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'var(--color-surface-raised)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
        >
          <div className="font-semibold mb-1">
            {new Date(hoveredDate).toLocaleDateString('en-US', {
              month: 'short', day: 'numeric', year: 'numeric',
            })}
          </div>
          {hoveredEvents.slice(0, 3).map((e) => {
            const config = UPCOMING_EVENT_TYPE_CONFIG[e.event_type];
            return (
              <div key={e.id} className="flex items-center gap-1.5 py-0.5">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: config.color }}
                />
                <span className="truncate" style={{ maxWidth: 200 }}>{e.title}</span>
              </div>
            );
          })}
          {hoveredEvents.length > 3 && (
            <div style={{ color: 'var(--color-text-muted)' }}>
              +{hoveredEvents.length - 3} more
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px]" style={{ color: 'var(--color-text-muted)' }}>
        <span>Less</span>
        {['var(--color-surface-hover)', 'rgba(23,196,128,0.3)', 'rgba(23,196,128,0.55)', 'rgba(23,196,128,0.8)', 'rgba(23,196,128,1)'].map((color, i) => (
          <span
            key={i}
            className="inline-block rounded-sm"
            style={{ width: CELL_SIZE, height: CELL_SIZE, backgroundColor: color }}
          />
        ))}
        <span>More</span>
        <span className="ml-2">|</span>
        {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
          <span key={cat} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {cat}
          </span>
        ))}
      </div>
    </div>
  );
};

export default UpcomingCalendarModule;
